import React, { memo, useEffect, useMemo } from 'react';

import constants from 'constants/constants';
import produce from 'immer';
import { orderBy } from 'lodash';
import { Controller, UseFormReturn } from 'react-hook-form';

import { useQueryState } from 'hooks/useQueryState';
import useUserHook from 'hooks/useUserHook';

import { useGlobalFilterDataStore } from 'store/globalFilterData.store';
import { useProjectTreeStore } from 'store/projectContract.store';

import { useQueryGetStoredTags } from 'api/queries/getStoredTags.query';

import { IState } from 'components/common/GlobalFilterComponent/GlobalFilterComponent';
import classes from 'components/common/GlobalFilterComponent/GlobalFilterComponent.module.scss';
import { FilterLabels } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import { OptionsWithExclude } from 'components/common/MyCustomSelect/components/OptionsWithExclude/OptionsWithExclude';

import { Select } from 'components/DesignSystem/Select';

interface ITagsFilterProps {
    methods?: UseFormReturn<any, any>;
    name: string;
    setState?: React.Dispatch<React.SetStateAction<IState>>;
    handleFilters?: () => void;
    skipFormSet?: boolean;
}

interface Value {
    name: string;
    isExclude: boolean;
}

export const TagsFilter = memo(({ methods, name, setState, handleFilters, skipFormSet = false }: ITagsFilterProps) => {
    const [queryStringValue] = useQueryState('tags');
    const { getValueFromStore } = useGlobalFilterDataStore();
    const storeValue = getValueFromStore<ISelectOption<Value>[]>('tags');
    const { customerId } = useUserHook();
    const { project, contract } = useProjectTreeStore();
    const { data: tags } = useQueryGetStoredTags({
        enabled: true,
        customerId,
        projectId: project?.id,
        contractId: contract?.id,
    });

    const options = useMemo<ISelectOption<Value>[]>(
        () =>
            tags
                ? orderBy(
                      tags.map((item) => ({ label: item.name, value: { name: item.name, isExclude: false } })),
                      [(item: any) => item['label'].toLowerCase()],
                      ['asc'],
                  )
                : [],
        [tags],
    );

    const isValue = useMemo(() => {
        if (queryStringValue && Array.isArray(queryStringValue) && queryStringValue.length > 0) {
            return queryStringValue;
        } else {
            return storeValue;
        }
    }, [queryStringValue, storeValue]);

    useEffect(() => {
        if (setState) {
            if (!isValue || isValue.length === 0) {
                setState((prev) => {
                    return produce(prev, (draft) => {
                        draft.tags.isReady = true;
                        draft.tags.isEmpty = true;
                        return draft;
                    });
                });
            }

            if (isValue && isValue.length > 0 && !skipFormSet) {
                const convertStringToBoolean = isValue.map((item) => {
                    return {
                        ...item,
                        value: {
                            name: item.value.name,
                            isExclude: String(item.value.isExclude).toLowerCase() === 'true',
                        },
                    };
                });
                methods?.setValue(name, convertStringToBoolean);

                setState((prev) => {
                    return produce(prev, (draft) => {
                        draft.tags.isReady = true;
                        return draft;
                    });
                });
            }
        }
    }, [isValue, setState, methods, name]);

    const handleChange = (value: readonly ISelectOption<Value>[]) => {
        methods?.setValue(name, value);
        handleFilters && handleFilters();
    };

    const handleValue = (value) => {
        return orderBy(value, (o) => o.value.name.toLowerCase());
    };

    return (
        <div className={classes.selectWrapper}>
            <div data-testid={'tags-filter'}>
                <Controller
                    render={({ field }) => (
                        <Select<ISelectOption<Value>, true>
                            {...field}
                            options={options}
                            isMulti
                            onChange={handleChange}
                            value={handleValue(field.value)}
                            placeholder={'Select Tags...'}
                            components={{
                                Option: OptionsWithExclude,
                            }}
                            label={FilterLabels.TAG_CARD_BOARD}
                        />
                    )}
                    name={name}
                    control={methods?.control}
                />
            </div>
        </div>
    );
});
